import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Users, 
  BookOpen, 
  GraduationCap,
  FileText, 
  ChevronRight,
  Loader2,
  AlertCircle,
  TrendingUp,
  UserPlus,
  Settings,
  BarChart3,
  Bell
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch admin stats
        const statsRes = await fetch(`${API_URL}/api/portal/admin/stats`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData);
        } else {
          // Fallback stats if endpoint doesn't exist yet
          setStats({
            totalStudents: 0,
            totalInstructors: 0,
            totalPrograms: 0,
            totalCourses: 0,
            totalAssignments: 0,
            activeEnrollments: 0
          });
        }
        
        // Fetch recent users
        const usersRes = await fetch(`${API_URL}/api/portal/auth/users?limit=5`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          setRecentUsers(usersData.users || []);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching admin data:', err);
        setError('Failed to load dashboard data');
        setLoading(false);
      }
    };

    if (token) {
      fetchData();
    }
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const statCards = [
    { 
      label: 'Total Students', 
      value: stats?.totalStudents || recentUsers.filter(u => u.role === 'student').length, 
      icon: GraduationCap, 
      color: 'bg-blue-500',
      link: '/admin/students'
    },
    { 
      label: 'Instructors', 
      value: stats?.totalInstructors || recentUsers.filter(u => u.role === 'instructor').length, 
      icon: Users, 
      color: 'bg-emerald-500',
      link: '/admin/instructors'
    },
    { 
      label: 'Programs', 
      value: stats?.totalPrograms || 1, 
      icon: BookOpen, 
      color: 'bg-purple-500',
      link: '/admin/programs'
    },
    { 
      label: 'Courses', 
      value: stats?.totalCourses || 4, 
      icon: FileText, 
      color: 'bg-orange-500',
      link: '/admin/courses'
    },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">
          Admin Dashboard
        </h1>
        <p className="text-purple-100">
          Welcome back, {user?.name?.split(' ')[0] || 'Admin'}! Manage the entire platform from here.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Link key={stat.label} to={stat.link}>
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center`}>
                    <stat.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                    <p className="text-sm text-gray-500">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Admin Actions Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* User Management */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              User Management
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/admin/students" className="block">
              <Button variant="outline" className="w-full justify-start gap-3">
                <GraduationCap className="w-5 h-5 text-blue-600" />
                <span>Manage Students</span>
                <ChevronRight className="w-4 h-4 ml-auto" />
              </Button>
            </Link>
            <Link to="/admin/instructors" className="block">
              <Button variant="outline" className="w-full justify-start gap-3">
                <Users className="w-5 h-5 text-emerald-600" />
                <span>Manage Instructors</span>
                <ChevronRight className="w-4 h-4 ml-auto" />
              </Button>
            </Link>
            <Button variant="outline" className="w-full justify-start gap-3">
              <UserPlus className="w-5 h-5 text-purple-600" />
              <span>Create New User</span>
            </Button>
          </CardContent>
        </Card>

        {/* Academic Management */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-600" />
              Academic Management
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/admin/programs" className="block">
              <Button variant="outline" className="w-full justify-start gap-3">
                <GraduationCap className="w-5 h-5 text-purple-600" />
                <span>Manage Programs</span>
                <ChevronRight className="w-4 h-4 ml-auto" />
              </Button>
            </Link>
            <Link to="/admin/courses" className="block">
              <Button variant="outline" className="w-full justify-start gap-3">
                <BookOpen className="w-5 h-5 text-blue-600" />
                <span>Manage Courses</span>
                <ChevronRight className="w-4 h-4 ml-auto" />
              </Button>
            </Link>
            <Link to="/admin/assignments" className="block">
              <Button variant="outline" className="w-full justify-start gap-3">
                <FileText className="w-5 h-5 text-orange-600" />
                <span>All Assignments</span>
                <ChevronRight className="w-4 h-4 ml-auto" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* System Tools */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Settings className="w-5 h-5 text-purple-600" />
              System Tools
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/admin/announcements" className="block">
              <Button variant="outline" className="w-full justify-start gap-3">
                <Bell className="w-5 h-5 text-yellow-600" />
                <span>Broadcast Announcement</span>
              </Button>
            </Link>
            <Link to="/admin/reports" className="block">
              <Button variant="outline" className="w-full justify-start gap-3">
                <BarChart3 className="w-5 h-5 text-emerald-600" />
                <span>View Reports</span>
                <ChevronRight className="w-4 h-4 ml-auto" />
              </Button>
            </Link>
            <Link to="/admin/settings" className="block">
              <Button variant="outline" className="w-full justify-start gap-3">
                <Settings className="w-5 h-5 text-gray-600" />
                <span>Platform Settings</span>
                <ChevronRight className="w-4 h-4 ml-auto" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Recent Users */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg font-semibold">Recent Portal Users</CardTitle>
          <Link to="/admin/students">
            <Button variant="ghost" size="sm">
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {recentUsers.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No users found</p>
          ) : (
            <div className="space-y-3">
              {recentUsers.map((portalUser) => (
                <div 
                  key={portalUser.user_id} 
                  className="flex items-center justify-between p-3 rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                      portalUser.role === 'admin' ? 'bg-purple-600' :
                      portalUser.role === 'instructor' ? 'bg-emerald-600' : 'bg-blue-600'
                    }`}>
                      {portalUser.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{portalUser.name}</p>
                      <p className="text-sm text-gray-500">{portalUser.email}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    portalUser.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                    portalUser.role === 'instructor' ? 'bg-emerald-100 text-emerald-700' : 
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {portalUser.role}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
